import { hasLocalePrefix } from '@/router';
import { stripHtml } from '@worldcoo/shared/utils';
import { useEffect } from 'react';

interface HreflangAlternate {
	hreflang: string;
	href: string;
}

/** Map short locale codes to BCP 47 og:locale values. */
const localeToOgLocale: Record<string, string> = {
	es: 'es_ES',
	en: 'en_US',
	ca: 'ca_ES',
	it: 'it_IT',
	de: 'de_DE',
	fr: 'fr_FR',
	pt: 'pt_PT',
	nl: 'nl_NL',
	eu: 'eu_ES',
	gl: 'gl_ES',
};

/**
 * Sets SEO meta tags via both React elements (for SSR) and DOM API (for client).
 *
 * React 19 Float hoists <meta>/<link>/<script> elements to <head> during SSR, which is correct.
 * However, because head loading is deferred (for load-performance reasons), during client-side
 * hydration Float removes these tags when Suspense boundaries replay — even if the data is
 * available. The inline `useEffect` re-applies the tags after hydration so they survive in
 * the final DOM.
 */
export const Seo = (props: {
	title?: string;
	description?: string;
	image?: string;
	canonicalUrl?: string;
	jsonLd?: Record<string, unknown>[];
	alternates?: HreflangAlternate[];
	/** Current locale code (e.g., 'es') — used for og:locale */
	locale?: string;
	/** Other available locale codes — used for og:locale:alternate */
	alternateLocales?: string[];
	/** Site name shown in og:site_name (e.g., portal or merchant name) */
	siteName?: string;
	/** When true, renders <meta name="robots" content="noindex"> */
	noindex?: boolean;
	/** Override og:type (default: 'website'). Use 'article' for campaign pages. */
	ogType?: string;
	/** Article-specific Open Graph metadata (only used when ogType='article'). */
	articleMeta?: {
		publishedTime?: string;
		tags?: string[];
	};
}) => {
	const description = props.description ? stripHtml(props.description) : undefined;
	const ogLocale = props.locale
		? (localeToOgLocale[props.locale] ?? `${props.locale}_${props.locale.toUpperCase()}`)
		: undefined;
	const ogAlternateLocales = props.alternateLocales
		?.filter((l) => l !== props.locale)
		.map((l) => localeToOgLocale[l])
		.filter((v): v is string => Boolean(v));

	// Re-apply meta tags after hydration to survive React 19 Float cleanup
	useEffect(() => {
		if (props.title) document.title = props.title;
		setMeta('name', 'description', description);
		setMeta('name', 'robots', props.noindex ? 'noindex' : undefined);
		setMeta('name', 'twitter:title', props.title);
		setMeta('name', 'twitter:description', description);
		setMeta('name', 'twitter:creator', '@worldcoo');
		setMeta('name', 'twitter:site', '@worldcoo');
		setMeta('property', 'og:type', props.ogType ?? 'website');
		setMeta('property', 'og:title', props.title);
		setMeta('property', 'og:description', description);
		setMeta('property', 'og:locale', ogLocale);
		setMeta('property', 'og:site_name', props.siteName);
		setMeta('property', 'og:url', props.canonicalUrl);
		setMeta('property', 'article:published_time', props.articleMeta?.publishedTime);
		setMeta('name', 'twitter:image', props.image);
		setMeta('name', 'twitter:card', props.image ? 'summary_large_image' : undefined);
		setMeta('property', 'og:image', props.image);
		setCanonical(props.canonicalUrl);
		syncAlternateLinks(props.alternates);
		syncMultiMeta('property', 'article:tag', props.articleMeta?.tags);
		syncMultiMeta('property', 'og:locale:alternate', ogAlternateLocales);
		syncJsonLd(props.jsonLd);
	});

	// SSR: React elements are hoisted to <head> by Float (for non-JS bots/crawlers)
	return (
		<>
			{props.title && <title>{props.title}</title>}
			{description && <meta name="description" content={description} />}
			{props.noindex && <meta name="robots" content="noindex" />}
			{props.canonicalUrl && <link rel="canonical" href={props.canonicalUrl} />}
			{props.alternates?.map((alt) => (
				<link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
			))}
			<meta name="twitter:title" content={props.title} />
			{description && <meta name="twitter:description" content={description} />}
			<meta name="twitter:creator" content="@worldcoo" />
			<meta name="twitter:site" content="@worldcoo" />
			<meta property="og:type" content={props.ogType ?? 'website'} />
			{props.articleMeta?.publishedTime && (
				<meta property="article:published_time" content={props.articleMeta.publishedTime} />
			)}
			{props.articleMeta?.tags?.map((tag) => (
				<meta key={tag} property="article:tag" content={tag} />
			))}
			<meta property="og:title" content={props.title} />
			{ogLocale && <meta property="og:locale" content={ogLocale} />}
			{props.alternateLocales
				?.filter((l) => l !== props.locale)
				.map((l) => localeToOgLocale[l])
				.filter(Boolean)
				.map((ogLoc) => (
					<meta key={ogLoc} property="og:locale:alternate" content={ogLoc} />
				))}
			{props.siteName && <meta property="og:site_name" content={props.siteName} />}
			{props.canonicalUrl && <meta property="og:url" content={props.canonicalUrl} />}
			{description && <meta property="og:description" content={description} />}
			{props.image && (
				<>
					<meta name="twitter:image" content={props.image} />
					<meta name="twitter:card" content="summary_large_image" />
					<meta property="og:image" content={props.image} />
				</>
			)}
			{props.jsonLd?.map((ld) => {
				const json = JSON.stringify(ld);
				return <script key={json} type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />;
			})}
		</>
	);
};

/**
 * Build hreflang alternate links where each locale may have a different path segment.
 * Used for campaign pages where slugs differ per language.
 * Falls back to `fallbackId` (UUID / route name) for locales without a slug so each
 * emitted URL still resolves via the app's slug-or-id lookup.
 * x-default is the pathless URL — the server detects locale from Accept-Language.
 */
export function buildAlternatesWithSlugs(
	baseUrl: string,
	locales: readonly string[],
	slugsByLocale: Record<string, string>,
	fallbackId: string,
): HreflangAlternate[] {
	const alternates: HreflangAlternate[] = locales.map((locale) => {
		const slug = slugsByLocale[locale] ?? fallbackId;
		return { hreflang: locale, href: `${baseUrl}/${locale}/${slug}` };
	});
	alternates.push({ hreflang: 'x-default', href: `${baseUrl}/${fallbackId}` });
	return alternates;
}

/**
 * Build hreflang alternate links for all supported locales + x-default.
 * x-default is unprefixed — the server auto-detects locale on pathless URLs.
 */
export function buildAlternates(baseUrl: string, path: string, locales: readonly string[]): HreflangAlternate[] {
	const cleanPath = path.startsWith('/') ? path : `/${path}`;
	const pathSuffix = cleanPath === '/' ? '' : cleanPath;
	const alternates: HreflangAlternate[] = locales.map((locale) => ({
		hreflang: locale,
		href: `${baseUrl}/${locale}${pathSuffix}`,
	}));
	alternates.push({ hreflang: 'x-default', href: `${baseUrl}${pathSuffix}` });
	return alternates;
}

/**
 * Resolve slug + canonical + alternates for a route whose slug varies per locale.
 * The canonical matches the page's actual URL: at `/${slug}` (unprefixed root) it's
 * `${baseUrl}/${slug}`; at `/${locale}/${slug}` it's the prefixed form. Lighthouse
 * flags the canonical as invalid when it matches a non-self hreflang alternate.
 */
export function resolveSlugSeo(
	baseUrl: string,
	locale: string,
	locales: readonly string[],
	slugsByLocale: Record<string, string>,
) {
	const slug = slugsByLocale[locale] ?? slugsByLocale.en!;
	return {
		slug,
		canonicalUrl: hasLocalePrefix() ? `${baseUrl}/${locale}/${slug}` : `${baseUrl}/${slug}`,
		alternates: buildAlternatesWithSlugs(baseUrl, locales, slugsByLocale, slug),
	};
}

// Only operate on nodes this module has created. Nodes hoisted into <head> by React 19's
// Float (from JSX like <meta>, <link>, <script>) are tracked in React's fiber tree — if
// we remove or move them, a later unmount walks triggers `Cannot read properties of null
// (reading 'removeChild')` because the node's parentNode is gone.
const MANAGED_ATTR = 'data-seo-managed';
const MANAGED_SELECTOR = `[${MANAGED_ATTR}="true"]`;

function setMeta(attr: 'name' | 'property', key: string, content: string | undefined) {
	const existing = document.head.querySelector(`meta[${attr}="${key}"]${MANAGED_SELECTOR}`);
	if (!content) {
		existing?.remove();
		return;
	}
	if (existing) {
		existing.setAttribute('content', content);
		return;
	}
	const el = document.createElement('meta');
	el.setAttribute(attr, key);
	el.setAttribute('content', content);
	el.setAttribute(MANAGED_ATTR, 'true');
	document.head.appendChild(el);
}

function setCanonical(href: string | undefined) {
	const existing = document.head.querySelector(`link[rel="canonical"]${MANAGED_SELECTOR}`);
	if (!href) {
		existing?.remove();
		return;
	}
	if (existing) {
		existing.setAttribute('href', href);
		return;
	}
	const el = document.createElement('link');
	el.setAttribute('rel', 'canonical');
	el.setAttribute('href', href);
	el.setAttribute(MANAGED_ATTR, 'true');
	document.head.appendChild(el);
}

function syncAlternateLinks(alternates: HreflangAlternate[] | undefined) {
	for (const el of document.head.querySelectorAll(`link[rel="alternate"][hreflang]${MANAGED_SELECTOR}`)) el.remove();
	if (!alternates) return;
	for (const alt of alternates) {
		const el = document.createElement('link');
		el.setAttribute('rel', 'alternate');
		el.setAttribute('hreflang', alt.hreflang);
		el.setAttribute('href', alt.href);
		el.setAttribute(MANAGED_ATTR, 'true');
		document.head.appendChild(el);
	}
}

function syncMultiMeta(attr: 'name' | 'property', key: string, values: string[] | undefined) {
	for (const el of document.head.querySelectorAll(`meta[${attr}="${key}"]${MANAGED_SELECTOR}`)) el.remove();
	if (!values) return;
	for (const value of values) {
		const el = document.createElement('meta');
		el.setAttribute(attr, key);
		el.setAttribute('content', value);
		el.setAttribute(MANAGED_ATTR, 'true');
		document.head.appendChild(el);
	}
}

function syncJsonLd(jsonLd: Record<string, unknown>[] | undefined) {
	// Only remove JSON-LD scripts that WE created (managed). React-rendered <script> nodes
	// in <body> are tracked by React's fiber tree — removing them causes "removeChild" errors
	// when React tries to unmount them during route transitions.
	for (const el of document.querySelectorAll(`script[type="application/ld+json"]${MANAGED_SELECTOR}`)) {
		el.remove();
	}
	if (!jsonLd) return;
	for (const ld of jsonLd) {
		const el = document.createElement('script');
		el.setAttribute('type', 'application/ld+json');
		el.setAttribute(MANAGED_ATTR, 'true');
		el.textContent = JSON.stringify(ld);
		document.head.appendChild(el);
	}
}
